import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable, catchError, map, of, throwError } from 'rxjs';
import { DOCUMENT } from '@angular/common';

import { AppService } from './app.service';

import { Story } from './shared/interfaces/story';
import { PhotoProfile } from './shared/interfaces/photo-profile';
import { StoryImage } from './shared/interfaces/story-image';
import { User } from './shared/interfaces/user';

@Injectable({
  providedIn: 'root'
})
export class StoriesService {
  private _localStorage: Storage | null = null

  tempStoryFiles: StoryImage[] = []
  storyId!: number
  storyEditionClosed = new BehaviorSubject<boolean>(false)
  story = new BehaviorSubject<Story | null>(null)
  storyOwnedByMe = false
  flaggedStoryId: Story['id'] | null = null
  blockedUserId: User['id'] | null = null
  blockedWithReportUserId: User['id'] | null = null
  storyMessage = new BehaviorSubject<{
    origin: 'edit' | 'create' | null,
    type: 'posted' | 'onHold' | null
  }>({
    origin: null,
    type: null
  })

  constructor(
    private http: HttpClient,
    private appService: AppService,
    @Inject(DOCUMENT) private document: Document,
  ) { 
    this._localStorage = this.document.defaultView?.localStorage || null
  }

  get(key: string): string {
    return this._localStorage?.getItem(key) || ''
  }

  set(key: string, value: string): void {
    this._localStorage?.setItem(key, value)
  }

  createStory(): Observable<number>{
    console.log('createStory')
    const body = {}

    return this.http.post<{ story: { id: number } }>(`${this.appService.API}/api/story`, body).pipe(
      map(res => {
        return res.story.id;
      }),
      catchError((error) => {
        return throwError(() => error)
      }),
    )
  }

  addImageToStory(data: { origin: string; mime: string}, storyId: number): Observable<{ photo: { id: number, signed_url: string, url: string }}> {
    const body = {
      type: "story",
      mime_type: data.mime,
      story_id: storyId
    }

    return this.http.post<{ photo: { id: number, signed_url: string, url: string }}>(`${this.appService.API}/api/photo`, body).pipe(
      map(res => {
        return res
      }),
      catchError((error) => {
        return throwError(() => error)
      }),
    )
  }

  publishStory(storyId: number | null): Observable<number | void> {
    if(!storyId) return of()

    const body = {
      story_id: storyId.toString(),
    }

    return this.http.put<{story: {id: number}}>(`${this.appService.API}/api/story`, body)
      .pipe(
        map(res => {
          return res.story.id
        }),
        catchError((error) => {
          return throwError(() => error)
        }),
      )
  }

  getPhotos(page: number, offset: number): Observable<PhotoProfile[]> {
    return this.http
      .get<{ photos: PhotoProfile[] }>(`${this.appService.API}/photo/criterias/${page}/${offset}`)
      .pipe(
        map(res => {
          res.photos.map((p) => (p.created_at *= 1000))
          return res.photos
        }),
        catchError(error => {
          return throwError(() => error)
        })
      )
  }

  getStories(page: number, offset: number): Observable<{ stories: Story[], total: number}>{
    return this.http.get<{ stories: Story[]; last_stories: Story[] ; total: number}>(`${this.appService.API}/api/story/criterias/${page}/${offset}`)
    .pipe(
      map(res => {
        res.stories.map((s) => {
          if(!s.created_at) return
          s.created_at *= 1000
        })
        if(res.last_stories){
          res.last_stories.map((ls) => {
            if(!ls.created_at) return
            ls.created_at *= 1000
          })
        }

        const stories = res.stories

        if(res.last_stories && res.stories.length < 10){ 
          res.last_stories.forEach(s => {
            if(stories.length < 10){
              stories.push(s)
            }
          })
        }

        return { stories: stories, total: res.total}
      }),
      catchError(error => {
        return throwError(() => error)
      })
    )
  }

  getStoriesByUser(page: number, offset: number, id?: number): Observable<Story[]> {
    let user!: User
    if(!id) {
      user = JSON.parse(this.get('user'))
    }

    return this.http.get<{ stories: Story[]; last_stories?: Story[] }>(`${this.appService.API}/api/story/user/${id ? id : user.id}/${page}/${offset}`)
    .pipe(
      map(res => {
        res.stories.map((s) => {
          if(!s.created_at) return
          s.created_at *= 1000
        })

        if(res.last_stories){
          res.last_stories.map((ls) => {
            if(!ls.created_at) return
            ls.created_at *= 1000
          })
  
          if (res.last_stories.length > 0) {
            res.stories.push({ id: null, likes: null, photos: [], type: 'text' })
            res.stories = res.stories.concat(res.last_stories)
          }
        }
        
        return res.stories
      }),
      catchError(error => {
        return throwError(() => error)
      })
    )
  }

  getStoryById(id: number): Observable<Story> {
    return this.http.get<{ story: Story }>(`${this.appService.API}/api/story/get/${id}`).pipe(
      map(res => {
        return res.story
      }),
      catchError((error) => {
        return throwError(() => error)
      })
    )
  }

  deleteStory(type: string, id: number): Observable<{code: number}> {
    console.log('deleteStory')
    return this.http.delete<{code: number}>(`${this.appService.API}/api/story?story_id=${id}`).pipe(
      map(res => {
        return res
      }),
      catchError((error) => {
        return throwError(() => error)
      })
    )
  }

  addCrushOnStory(storyId: number, type: string): Observable<number>{
    const body = {
      id: storyId,
      type: type
    }

    return this.http.post(`${this.appService.API}/api/crush/like`, body).pipe(
      map(res => {
        console.log(res)
        return storyId
      }),
      catchError((error) => {
        return throwError(() => error)
      })
    )
  }

  removeCrushOnStory(storyId: number, type: string): Observable<number>{
    return this.http.delete(`${this.appService.API}/api/crush/dislike?id=${storyId}&type=${type}`).pipe(
      map(res => {
        console.log(res)
        return storyId
      }),
      catchError((error) => {
        return throwError(() => error)
      })
    )
  }

  getCrushesByStory(storyId: number, type: string): Observable<User[]>{
    return this.http.get<{users: User[]}>(`${this.appService.API}/api/crush/content/${type}/${storyId}/1/10`).pipe(
      map(res => {
        return res.users
      }),
      catchError((error) => {
        return throwError(() => error)
      })
    )
  }

  deletePhoto(photoId: number): Observable<{ code: number }> {
    return this.http.delete<{ code: number }>(`${this.appService.API}/api/photo?photo_id=${photoId.toString()}`).pipe(
      map((res) => {
        return res
      }),
      catchError((error) => {
        return throwError(() => error)
      }),
    )
  }
}
